import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import {
  PRICE_SCHEDULE_MODES,
  ROUTE_PATHS,
  LEGACY_SCREEN_VIEW,
  NO_CHANGE_OF_INFO
} from "../../common_nextgen/utils/Constants";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import ConfirmationModal from "../../common_nextgen/common_components/Modal/ConfirmationModal";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import { useModal } from "../../common_nextgen/hooks/ModalContext";
import useLookupNavigation from "../../common_nextgen/common_components/common-hooks/useLookupNavigation";

const formFields = [
  {
    section: "Section 1",
    name: "opcScheduleName",
    label: "Schedule",
    fieldLabel: "OPC Schedule Name",
    type: "text",
    options: {
      required: { value: true, message: "Value required" },
      maxLength: {
        value: 10,
        message: "Cannot exceed more than 10 characters",
      },
    },
    fieldNature: "Add: Read-only, Change: Read-only, View: Read-only",
    position: [4, 24],
  },
  {
    section: "Section 1",
    name: "opcDescription",
    label: "Description",
    fieldLabel: "Description",
    type: "text",
    options: {
      required: { value: true, message: "Value required" },
      maxLength: {
        value: 40,
        message: "Cannot exceed more than 40 characters",
      },
    },
    fieldNature: "Add: Read-only, Change: Read-only, View: Read-only",
    position: [5, 24],
  },
  {
    section: "Section 1",
    name: "opdStepNumber",
    label: "Step Number",
    fieldLabel: "OPD Step Number",
    type: "number",
    options: {
      required: { value: true, message: "Value required" },
      min: { value: 0, message: "Cannot be less than 0" },
      max: { value: 99, message: "Cannot exceed more than 99" },
    },
    fieldNature: "Add: Read-only, Change: Read-only, View: Read-only",
    position: [6, 24],
  },
  {
    section: "Section 1",
    name: "oppFromDate",
    label: "From Date",
    fieldLabel: "From Date",
    type: "date",
    options: {
      required: { value: true, message: "Value required" },
      format: "MM/DD/YYYY",
    },
    fieldNature: "Add: Editable, Change: Editable, View: Read-only",
    position: [8, 12],
  },
  {
    section: "Section 1",
    name: "oppThruDate",
    label: "Thru Date",
    fieldLabel: "Thru Date",
    type: "date",
    options: {
      required: { value: true, message: "Value required" },
      format: "MM/DD/YYYY",
    },
    fieldNature: "Add: Editable, Change: Editable, View: Read-only",
    position: [8, 35],
  },
  {
    section: "Section 2",
    name: "oppPharmPatpayschedule",
    label: "Pharmacy Patient Pay",
    fieldLabel: "Pharmacy Patient Pay",
    type: "lookup",
    externalProgram: { default: "RCPS3001" },
    params: {
      lookupType: "external",
      // viewType:"legacy",
      viewType: "external",
      fieldMapping: {
        default: [
          {
            source: "oppPharmPatpayschedule",
            target: "ps3PatientPaySchName",
            legacy: "ps3PatientPaySchName",
          },
          {
            source: "wrkDescription251",
            target: "ps3PatientPaySchDesc",
            legacy: "ps3PatientPaySchDesc",
          },
        ],
      },
      appName: "PATIENT_PAY",
      pageName: "PATIENT_PAY_SCHEDULE",
    },
    options: {
      maxLength: {
        value: 10,
        message: "Cannot exceed more than 10 characters",
      },
    },
    fieldNature: "Add: Editable, Change: Editable, View: Read-only",
    position: [11, 24],
  },
  {
    section: "Section 2",
    name: "wrkDescription251",
    label: "",
    fieldLabel: "Wrk Description 25 1",
    type: "text",
    options: {
      maxLength: { value: 25, message: "MAXLENGTH_25" },
    },
    fieldNature: "Add: Read-Only, Change: Read-Only, View: Read-only",
    position: [11, 27],
  },
  {
    section: "Section 2",
    name: "oppClientPatpayschedule",
    label: "Client Patient Pay",
    fieldLabel: "Client Patient Pay",
    type: "lookup",
    externalProgram: { default: "RCPS3001" },
    params: {
      lookupType: "external",
      viewType: "external",
      // viewType:"legacy",
      fieldMapping: {
        default: [
          {
            source: "oppClientPatpayschedule",
            target: "ps3PatientPaySchName",
            legacy: "ps3PatientPaySchName",
          },
          {
            source: "wrkDescription252",
            target: "ps3PatientPaySchDesc",
            legacy: "ps3PatientPaySchDesc",
          },
        ],
      },
      appName: "PATIENT_PAY",
      pageName: "PATIENT_PAY_SCHEDULE",
    },
    options: {
      maxLength: {
        value: 10,
        message: "Cannot exceed more than 10 characters",
      },
    },
    fieldNature: "Add: Editable, Change: Editable, View: Read-only",
    position: [12, 24],
  },
  {
    section: "Section 2",
    name: "wrkDescription252",
    label: "",
    fieldLabel: "Wrk Description 25 2",
    type: "text",
    options: {
      maxLength: { value: 25, message: "MAXLENGTH_25" },
    },
    fieldNature: "Add: Read-Only, Change: Read-Only, View: Read-only",
    position: [12, 27],
  },
  {
    section: "Section 2",
    name: "oppStatus",
    label: "Status",
    fieldLabel: "Status (A/I)",
    type: "dropdown",
    dropdownCondition: "Valid Values",
    fieldNature: "Add: Read-only, Change: Read-only, View: Read-only",
    position: [14, 15],
  },
  {
    section: "Audit Fields",
    name: "addDate",
    label: "Added",
    fieldLabel: "ADD Date",
    type: "date",
    fieldNature: "Add: Non-Display, Change: Read-only, View: Read-only",
    position: [21, 20],
  },
  {
    section: "Audit Fields",
    name: "chgDate",
    label: "Changed",
    fieldLabel: "CHG Date",
    type: "date",
    fieldNature: "Add: Non-Display, Change: Read-only, View: Read-only",
    position: [21, 40],
  },
  {
    section: "Audit Fields",
    name: "chgUserName",
    label: "User",
    fieldLabel: "CHG User Name",
    type: "text",
    fieldNature: "Add: Non-Display, Change: Read-only, View: Read-only",
    position: [21, 57],
  },
];

const screenInfo = {
  ADD: ["Add OOP Patient Pay Schedules", "RCOPP002"],
  CHANGE: ["OOP Patient Pay Schedules", "RCOPP003"],
  VIEW: ["OOP Patient Pay Schedules", "RCOPP003"],
};

const OopPatientPaySchedulesForm = ({ mode }) => {
  const location = useLocation();
  const { axios_helper_no_header } = useAxios();
  /* const { selectedPriceSchedules = [], previousScreen = '/price-schedule', currentIndex: initialIndex = 0,
    opcScheduleName,
    opdStepNumber,
    oppSeqNbr
   } = location.state || {};  */
  /* const { state: stateVal } = location;
  const [currentState = {}] = [...stateVal].reverse();
  const previousState = [...stateVal];
  previousState.splice(stateVal.length - 1, 1);
  console.log("current state in form", currentState, previousState);*/
  const [stateVal, setStateVal] = useState(location.state || []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return {
      previousState,
      currentState,
    };
  }, [stateVal]);
  /* const {
    selectedPatientPaySchedules = [],
    previousScreen = ROUTE_PATHS.OUT_OF_POCKET_MAX_SCHEDULE,
    currentIndex: initialIndex = 0,
    opcScheduleName,
    opdStepNumber,
  } = currentState;*/
  const {
    selectedPatientPaySchedules = [],
    previousScreen = ROUTE_PATHS.OUT_OF_POCKET_MAX_SCHEDULE,
    currentIndex: initialIndex = 0,
    opcScheduleName,
    opdStepNumber,
    lookupScreen,
    lookupParams,
    lookupMode: initialLookupMode = false,
    formData: baseFormData = {},
    navigateData,
  } = useMemo(() => {
    const {
      selectedPatientPaySchedules,
      previousScreen,
      currentIndex,
      opcScheduleName,
      opdStepNumber,
      initialParamsData,
      lookupScreen,
      lookupParams,
      lookupMode,
      formData,
      navigateData,
    } = currentState;
    return {
      selectedPatientPaySchedules,
      previousScreen,
      currentIndex,
      opcScheduleName,
      opdStepNumber,
      initialParamsData,
      lookupScreen,
      lookupParams,
      lookupMode,
      formData,
      navigateData,
    };
  }, [currentState]);
  const navigateToPage = useCustomNavigate();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const toast = useToast();

  /* const { showModal } = useModal();
  const [lookupData, setLookupData] = useState({});
  const handleReceiveData = (data) => {
    //setInitialFormData({ ...initialFormData, ...data });
    if (data && Object.keys(data).length) {
      setLookupData(data);
    }
  };
  const fetchLookupData = (lookupScreen, params, formData) => {
    showModal(lookupScreen, { params }, handleReceiveData, formData);
  };*/

  const {
    lookupData,
    fetchLookupData,
    viewLookupDetails,
    lookupMode,
    setLookupMode,
  } = useLookupNavigation({
    initialFormData,
    initialLookupMode,
    initialIndex: currentIndex,
    lookupScreen,
    lookupParams,
    navigateData,
    previousState,
    currentState,
  });

  const handleBack = () => {
    if (currentIndex < selectedPatientPaySchedules.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setLookupMode(false);
      setCurrentMode(mode);
    } else {
      navigateToPage(previousScreen, previousState);
    }
  };

  const getPageTitle = useCallback(() => {
    const [screenTitle, screenNumber] = isAddMode(currentMode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  }, [currentMode]);

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";
    let infoChanged = true;

    if (isAddMode(currentMode)) {
      url = "api/oppMaxDtlPatPaySch/create";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/oppMaxDtlPatPaySch/update";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];
    

    if (JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)) {
      infoChanged = false;
    }
  }

    const data = { ...dataForSubmission, screenNumber, infoChanged };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      if (responseStatus.message === NO_CHANGE_OF_INFO) {
                         toast.current.show({
                           severity: "info",
                           summary: "",
                           detail: responseStatus.message,
                           life: 3000,
                         });
                       } else {
                         toast.current.show({
                           severity: "success",
                           summary: "",
                           detail: responseStatus.message,
                           life: 3000,
                         });
      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      }
    } }catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};

    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => {
        setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
        setLookupMode(false);
      };
      keyName.chmod = "View";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => editValidation();
      keyName.chmod = "Edit";
    }

    return [keyFunc, keyName];
  }, [
    currentMode,
    currentIndex,
    previousState,
    currentState,
    previousScreen,
    setLookupMode,
  ]);

  const editValidation = () => {
    const params = {
      opcScheduleName: opcScheduleName,
      opdStepNumber: opdStepNumber,
      oppSeqNbr: selectedPatientPaySchedules[currentIndex].oppSeqNbr,
    };
    axios_helper_no_header(
      "GET",
      "api/oppMaxDtlPatPaySch/validateBeforeEdit",
      null,
      params
    )
      .then((response) => {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      })
      .catch((error) => {
        console.error("There was an error editing the data!", error);
        const { responseStatus: { displayErrorFields = null, message } = {} } =
          error.data;
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      });
  };

  /*const viewLookupDetails = (lookupScreen, params, formData) => {
    const { viewType, fieldMapping } = params;
    const { default: screenNumber } = lookupScreen;
    if (viewType === "legacy") {
      const { default: viewParams } = fieldMapping;
      const paramData = viewParams.reduce((acc, item) => {
        acc[item["legacy"]] = formData[item["source"]];
        return acc;
      }, {});
      const message = {
        message: "navigate-to-presto",
        programCode: LEGACY_SCREEN_VIEW[screenNumber],
        parameters: paramData,
      };
      console.log("message", message);
      window.top?.postMessage(message, "*");
    } else {
      showModal(lookupScreen, { params }, null, formData, true);
    }
  };*/

  useEffect(() => {
    const fetchInitialData = async () => {
      let url = isAddMode(currentMode)
        ? `api/oppMaxDtlPatPaySch/initial-form-data/${encodeURIComponent(
            opcScheduleName
          )}?opdStepNumber=${opdStepNumber}`
        : `api/oppMaxDtlPatPaySch/${encodeURIComponent(
            opcScheduleName
          )}?opdStepNumber=${opdStepNumber}&oppSeqNbr=${
            selectedPatientPaySchedules[currentIndex].oppSeqNbr
          }`;

      try {
        const { responseData } = await axios_helper_no_header("GET", url);
        setInitialFormData(responseData);
      } catch (e) {
        console.log(e);
      }
    };

    /* if (
      !isAddMode(currentMode) ||
      (!Object.keys(baseFormData).length && selectedPatientPaySchedules.length)
    ) {
      fetchInitialData();
    } else {
      setInitialFormData(baseFormData);
    }*/
    if (
      (isAddMode(currentMode) && opdStepNumber) ||
      (!lookupMode && selectedPatientPaySchedules.length)
    ) {
      fetchInitialData();
    } else {
      setInitialFormData(baseFormData);
    }
  }, [mode, currentIndex]);

  useEffect(() => {
    if (
      Object.keys(baseFormData).length &&
      JSON.stringify(baseFormData) !== JSON.stringify(initialFormData)
    ) {
      setInitialFormData(baseFormData);
    }
  }, [baseFormData]);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const handleMessage = (event) => {
    const { type, data } = event.data;

    if (type === "set-params") {
      setStateVal(data);
      //setExternalMode(true);
    }
    console.log("message", event.data);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  return (
    <div className="grid container">
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
          fetchLookupData={fetchLookupData}
          lookupData={lookupData}
          viewLookupDetails={viewLookupDetails}
        />
      </div>
    </div>
  );
};

export default OopPatientPaySchedulesForm;